import { useLocation } from "react-router";
import { useGetMembershipShareAccessQuery } from "@lending/hooks/queries/MembershipShare/useGetMembershipShareAccessQuery";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useFlowType } from "@lending/hooks/useFlowType";
import { getCurrentFlowStepId } from "@lending/navigation";
import { isMembershipShareState } from "./membershipShareRoutePolicy";
import type { FlowType, ProductType } from "@lending/navigation/types";
import type { MembershipShareAccessResponse, StateEnum } from "@lending/types";

interface RouteResolutionApplication {
  state: StateEnum;
  account_application_uuid?: string | null;
}

interface QueryResult<TApplication> {
  data?: TApplication;
  isLoading: boolean;
  isError: boolean;
}

interface UseResolvedApplicationRouteOptions<TApplication extends RouteResolutionApplication> {
  productType: ProductType;
  useApplicationQuery: () => QueryResult<TApplication>;
  getCanonicalRoutePolicy: (
    application: TApplication,
    currentStepId: string | null,
    membershipShareAccess: MembershipShareAccessResponse | undefined,
    flowType?: FlowType
  ) => {
    canonicalStepId: string | null;
  };
}

function useResolvedApplicationRoute<TApplication extends RouteResolutionApplication>({
  productType,
  useApplicationQuery,
  getCanonicalRoutePolicy,
}: UseResolvedApplicationRouteOptions<TApplication>) {
  const location = useLocation();
  const flowType = useFlowType();
  const applicationUuid = useCurrentApplicationUuid();
  const {
    data: application,
    isLoading: applicationIsLoading,
    isError: applicationIsError,
  } = useApplicationQuery();

  const requiresMembershipShareAccess = isMembershipShareState(application?.state);

  const membershipShareAccessQuery = useGetMembershipShareAccessQuery({
    enabled: Boolean(application) && requiresMembershipShareAccess,
  });

  const currentStepId = applicationUuid
    ? getCurrentFlowStepId(location.pathname, productType, flowType, applicationUuid)
    : null;

  if (applicationIsLoading || !application) {
    return {
      isLoading: applicationIsLoading,
      isError: applicationIsError,
      canonicalStepId: null,
      currentStepId,
      applicationState: application?.state,
      application,
      accountApplicationUuid: application?.account_application_uuid,
      shouldAlwaysEnforceRoute: false,
    };
  }

  const membershipShareAccessIsReady =
    !requiresMembershipShareAccess ||
    membershipShareAccessQuery.isError ||
    Boolean(membershipShareAccessQuery.data);

  if (!membershipShareAccessIsReady) {
    return {
      isLoading: true,
      isError: false,
      canonicalStepId: null,
      currentStepId,
      applicationState: application.state,
      application,
      accountApplicationUuid: application.account_application_uuid,
      shouldAlwaysEnforceRoute: false,
    };
  }

  const canonicalStepId = getCanonicalRoutePolicy(
    application,
    currentStepId,
    membershipShareAccessQuery.data,
    flowType
  ).canonicalStepId;

  return {
    isLoading: false,
    isError: applicationIsError || membershipShareAccessQuery.isError,
    canonicalStepId,
    currentStepId,
    applicationState: application.state,
    application,
    accountApplicationUuid: application.account_application_uuid,
    shouldAlwaysEnforceRoute:
      requiresMembershipShareAccess && Boolean(membershipShareAccessQuery.data),
  };
}

export { useResolvedApplicationRoute };
